import React from 'react';
import { FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { BaseField } from './BaseField';
import type { BaseFieldProps } from './types';

/**
 * Single choice field component that handles:
 * - single_choice (form-builder)
 * - radio (legacy)
 */
export const SingleChoiceField: React.FC<BaseFieldProps> = ({
  field,
  value = '',
  onChange,
  onBlur,
  mode = 'edit',
  error,
  touched,
  sx,
  showLabel = true,
  disabled,
}) => {
  // Extract field properties based on field format
  const label = 'label' in field ? field.label : '';
  
  // Extract options based on field format
  let options: Array<{ label: string; value: string }> = [];
  
  if ('options' in field && field.options) {
    options = field.options.map((opt: any) => {
      if (typeof opt === 'string') {
        return { label: opt, value: opt };
      } else if ('label' in opt && 'value' in opt) {
        return { label: opt.label, value: String(opt.value) };
      } else if ('text' in opt) {
        return { label: opt.text, value: String(opt.value || opt.id || opt.text) };
      }
      return { label: String(opt), value: String(opt) };
    });
  }

  // Handle display mode
  if (mode === 'display') {
    const selectedOption = options.find(opt => opt.value === value);
    return (
      <BaseField field={field} mode={mode} sx={sx} showLabel={showLabel}>
        <Typography variant="body2">
          {selectedOption?.label || value || '-'}
        </Typography>
      </BaseField>
    );
  }

  // Handle edit/preview modes
  return (
    <BaseField
      field={field}
      mode={mode}
      error={error}
      touched={touched}
      sx={sx}
      showLabel={showLabel}
      disabled={disabled}
    >
      <RadioGroup
        value={value}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        onBlur={onBlur}
        aria-label={label}
        name={field.id}
      >
        {options.map((option, index) => (
          <FormControlLabel
            key={index}
            value={option.value}
            control={<Radio size="small" />}
            label={option.label}
            disabled={disabled || mode === 'preview'}
          />
        ))}
      </RadioGroup>
    </BaseField>
  );
};